import type {
  TimelineEvent,
  TimelineLabelMode,
  TimelineProtectedEvent,
} from "../contracts/event-timeline";
import { createDateFormatter, type TimezoneMode } from "@nexus/ui";

export const HOUR_MS = 3_600_000;
export const MINUTE_MS = 60_000;

/**
 * ClickHouse serialises event timestamps as naive UTC strings, e.g.
 * "2026-07-10 19:00:00". Normalise them to a real epoch by forcing a UTC offset
 * so the timeline stays aligned with `Date.now()`.
 */
export function parseDbTime(value: string): number {
  if (!value) return Number.NaN;
  let iso = value.includes("T") ? value : value.replace(" ", "T");
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso)) iso += "Z";
  return new Date(iso).getTime();
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function isOngoing(event: TimelineEvent, nowMs: number): boolean {
  const start = parseDbTime(event.event_start);
  const end = parseDbTime(event.event_end);
  return start <= nowMs && nowMs <= end;
}

export function formatClock(ms: number, timezone: TimezoneMode = "utc"): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "—";
  return createDateFormatter(timezone, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

export function formatDayClock(ms: number, timezone: TimezoneMode = "utc"): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "—";
  return createDateFormatter(timezone, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(date);
}

export function formatTimelineDay(ms: number, timezone: TimezoneMode = "utc"): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "—";
  return createDateFormatter(timezone, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

/**
 * "07:22 – 09:22 AM" rather than "07:22 AM – 09:22 AM": Intl collapses whatever
 * the two bounds share, in the user's locale.
 */
export function formatClockRange(
  startMs: number,
  endMs: number,
  timezone: TimezoneMode = "utc",
): string {
  const start = new Date(startMs);
  const end = new Date(endMs);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "—";
  try {
    const formatter = createDateFormatter(timezone, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }) as Intl.DateTimeFormat & {
      formatRange?: (start: Date, end: Date) => string;
    };
    if (typeof formatter.formatRange === "function" && endMs >= startMs) {
      return formatter.formatRange(start, end);
    }
  } catch {
    // Older engines, or an inverted range: fall through to the manual join.
  }
  return `${formatClock(startMs, timezone)} – ${formatClock(endMs, timezone)}`;
}

/** Ruler labels: hours when the window is short, days when it is long. */
export function formatTick(ms: number, spanMs: number, timezone: TimezoneMode = "utc"): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "—";
  if (spanMs <= 26 * HOUR_MS) return formatClock(ms, timezone);
  if (spanMs <= 4 * 24 * HOUR_MS) {
    return createDateFormatter(timezone, {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(date);
  }
  return createDateFormatter(timezone, { month: "short", day: "numeric" }).format(date);
}

export function formatDuration(startMs: number, endMs: number): string {
  const minutes = Math.max(0, Math.round((endMs - startMs) / MINUTE_MS));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
}

export function formatTimeTick(
  ms: number,
  spanMs: number,
  nowMs: number,
  labelMode: TimelineLabelMode,
  timezone: TimezoneMode = "utc",
): string {
  if (labelMode === "relative") {
    const deltaMs = ms - nowMs;
    const direction = deltaMs >= 0 ? "+" : "-";
    const absMinutes = Math.round(Math.abs(deltaMs) / MINUTE_MS);
    if (absMinutes < 1) return "T0";
    if (absMinutes < 60) return `T${direction}${absMinutes}m`;
    const absHours = Math.round(absMinutes / 60);
    if (absHours < 24) return `T${direction}${absHours}h`;
    return `T${direction}${Math.round(absHours / 24)}d`;
  }

  return formatTick(ms, spanMs, timezone);
}

export function formatEventTooltip(event: TimelineEvent, timezone: TimezoneMode = "utc"): string {
  const start = parseDbTime(event.event_start);
  const end = parseDbTime(event.event_end);

  return [
    event.event_label,
    formatTimelineDay(start, timezone),
    `Start: ${formatTimelineDay(start, timezone)} ${formatClock(start, timezone)}`,
    `End: ${formatTimelineDay(end, timezone)} ${formatClock(end, timezone)}`,
    `Duration: ${formatDuration(start, end)}`,
  ].join("\n");
}

/** "in 25m" / "in 3h" — relative time reads faster than a clock for what's next. */
export function formatRelative(fromMs: number, toMs: number): string {
  const minutes = Math.round((toMs - fromMs) / MINUTE_MS);
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return "now";
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}

export function titleCase(value: string): string {
  if (!value) return "—";
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * The tenant an event belongs to, as text. Tenant is a property, not a lens:
 * cross-tenant filtering already lives in the dashboard topbar.
 */
export function eventTenantLabel(event: TimelineEvent): string {
  const labels = new Set(
    event.protected_events.map((pe) => pe.tenant_label || `Tenant ${pe.tenant_id}`),
  );
  const [first] = [...labels];
  if (!first) return "";
  return labels.size > 1 ? `${first} +${labels.size - 1}` : first;
}

/** Problems first, then alphabetical — the SOC analyst's reading order. */
export function orderChannels(protectedEvents: TimelineProtectedEvent[]): TimelineProtectedEvent[] {
  return [...protectedEvents].sort((a, b) => {
    const byAnomaly = b.unresolved_anomaly_count - a.unresolved_anomaly_count;
    if (byAnomaly !== 0) return byAnomaly;
    return (a.channel || "").localeCompare(b.channel || "");
  });
}

/** Live events, the ones with problems first. Drives the collapsed summary. */
export function orderLiveEvents(events: TimelineEvent[], nowMs: number): TimelineEvent[] {
  return events
    .filter((event) => isOngoing(event, nowMs))
    .sort((a, b) => {
      const byAnomaly = b.unresolved_anomaly_count - a.unresolved_anomaly_count;
      if (byAnomaly !== 0) return byAnomaly;
      return parseDbTime(a.event_start) - parseDbTime(b.event_start);
    });
}

/** The next event to start. Used when nothing is running right now. */
export function nextUpcomingEvent(events: TimelineEvent[], nowMs: number): TimelineEvent | null {
  let best: TimelineEvent | null = null;
  let bestStart = Number.POSITIVE_INFINITY;
  for (const event of events) {
    const start = parseDbTime(event.event_start);
    if (start > nowMs && start < bestStart) {
      best = event;
      bestStart = start;
    }
  }
  return best;
}

/**
 * Greedy lane packing: assign each event to the first lane whose last event has
 * already ended, so overlapping events never visually collide.
 */
export function packLanes(events: TimelineEvent[]): Map<string, number> {
  const laneEnds: number[] = [];
  const assignment = new Map<string, number>();
  const sorted = [...events].sort(
    (a, b) => parseDbTime(a.event_start) - parseDbTime(b.event_start),
  );

  for (const event of sorted) {
    const start = parseDbTime(event.event_start);
    const end = parseDbTime(event.event_end);
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    assignment.set(event.event_id, lane);
  }

  return assignment;
}
